import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import {
  generateSentimentKeywords,
  getBreakdown,
  getDefaultSentimentKeywords,
  getSignals,
  getSentimentSummary,
  getTopKeywords
} from './api';
import { Breakdown, Signal, SentimentSummary, SignalKeywordItem, TopKeywordItem } from './types';
import { MetaApiResponse } from 'utils/types/CommonApiResponse';
import { useParams } from 'react-router-dom';

export function useGetSignals(
  params?: Record<string, any>,
  options?: UseQueryOptions<{ data: Signal[]; meta: MetaApiResponse }, Error>
) {
  const { locationId, agentId } = useParams<{ locationId: string; agentId: string }>();

  return useQuery<{ data: Signal[]; meta: MetaApiResponse }>({
    queryKey: ['signals', locationId, agentId, params],
    queryFn: () => getSignals(locationId!, agentId!, params),
    enabled: Boolean(locationId && agentId),
    ...options
  });
}
export function useGetDefaultSentimentKeywords(enabled: boolean = true) {
  return useQuery<{ data: SignalKeywordItem[] }>({
    queryKey: ['signals, sentimentKeywords', 'default'],
    queryFn: () => getDefaultSentimentKeywords(),
    enabled
  });
}

export function useGenerateSentimentKeywords() {
  return useMutation({
    mutationFn: (text: string) => generateSentimentKeywords(text)
  });
}

export function useGetSentimentSummary(
  params?: { start_date?: string; end_date?: string },
  options?: UseQueryOptions<SentimentSummary, Error>
) {
  const { locationId, agentId } = useParams<{ locationId: string; agentId: string }>();

  return useQuery<SentimentSummary>({
    queryKey: ['signals', 'sentiment_summary', locationId, agentId, params],
    queryFn: () => getSentimentSummary(locationId!, agentId!, params),
    enabled: Boolean(locationId && agentId),
    ...options
  });
}

export function useGetBreakdown(
  params?: { start_date?: string; end_date?: string; keywords?: string[] },
  options?: UseQueryOptions<Breakdown, Error>
) {
  const { locationId, agentId } = useParams<{ locationId: string; agentId: string }>();

  return useQuery<Breakdown>({
    queryKey: ['signals', 'breakdown', locationId, agentId, params],
    queryFn: () => getBreakdown(locationId!, agentId!, params),
    enabled: Boolean(locationId && agentId),
    ...options
  });
}

export function useGetTopKeywords(
  params?: { start_date?: string; end_date?: string },
  options?: UseQueryOptions<TopKeywordItem[], Error>
) {
  const { locationId, agentId } = useParams<{ locationId: string; agentId: string }>();

  return useQuery<TopKeywordItem[]>({
    queryKey: ['signals', 'top_keywords', locationId, agentId, params],
    queryFn: () => getTopKeywords(locationId!, agentId!, params),
    enabled: Boolean(locationId && agentId),
    ...options
  });
}
